import { GoodsType, MFAMethods, StoreContactType } from '@/config/constants';

import { App } from '@capacitor/app';
import { BookingFormState } from '@/store/state';
import { Contact } from '@/api/freedomQl/__generated__/graphql';
import { alertController } from '@ionic/vue';
import capConfig from '../../capacitor.config';
import config from '@/config';
import { integraUserToContact } from '../utils/helpers';
import router from '@/router';
import { signOut } from './auth';
import { t } from '@/utils/i18n';
import { useStore } from '@/store';

const companyTelephoneNumber = config.app.companyDetails.telephoneNumber;

const okAlertWithMessage = async (message: string) => {
  const alert = await alertController.create({
    message,
    buttons: [
      {
        text: t('base.ok'),
        role: 'confirm',
      },
    ],
  });

  await alert.present();
};

export const displayAdflexNavigationAlert = async (backPageName: string) => {
  const alert = await alertController.create({
    header: t('base.areYouSure'),
    message: t('base.payment.adflexNavWarning'),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
      },
      {
        text: t('base.ok'),
        role: 'destructive',
        cssClass: 'alert-destructive',
        handler: async () => {
          await router.replace({ name: backPageName });
        },
      },
    ],
  });

  await alert.present();
};

export const displaySignOutAlert = async () => {
  const alert = await alertController.create({
    header: t('base.areYouSure'),
    message: t('base.signOutConfirmation'),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
      },
      {
        text: t('base.ok'),
        role: 'destructive',
        cssClass: 'alert-destructive',
        handler: async () => {
          await signOut();
        },
      },
    ],
  });

  await alert.present();
};

export const displayExitAppAlert = async () => {
  const alert = await alertController.create({
    header: t('base.areYouSure'),
    message: t('base.exitAppConfirmation'),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
      },
      {
        text: t('base.ok'),
        role: 'destructive',
        cssClass: 'alert-destructive',
        handler: async () => {
          App.exitApp();
        },
      },
    ],
  });

  await alert.present();
};

export const displayDeleteAlert = async (func: () => Promise<void>) => {
  const alert = await alertController.create({
    header: t('base.areYouSure'),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
      },
      {
        text: t('base.ok'),
        role: 'destructive',
        cssClass: 'alert-destructive',
        handler: async () => {
          await func();
        },
      },
    ],
  });

  await alert.present();
};

export const displayFlightAirportAlert = async (
  stopCode: string,
  flightCode: string,
  isArrival: boolean,
) => {
  const alert = await alertController.create({
    header: t('base.flightSearch.flightWarning'),
    subHeader: `${t('base.flightSearch.selectedAirport', {
      airportCode: stopCode,
    })}, ${t('base.flightSearch.flightAirport', {
      airportCode: flightCode,
    })}`,
    message: t(
      isArrival
        ? 'base.flightSearch.airportArriveWarningMessage'
        : 'base.flightSearch.airportDepartWarningMessage',
    ),
    buttons: ['OK'],
  });

  await alert.present();
};

export const displayFlightTerminalAlert = async (
  stopCode: string,
  terminal: string,
  isArrival: boolean,
) => {
  const alert = await alertController.create({
    header: t('base.flightSearch.flightWarning'),
    subHeader: `${t('base.flightSearch.selectedAirport', {
      airportCode: stopCode,
    })}, ${t('base.flightSearch.flightTerminal', {
      terminal,
    })}`,
    message: t(
      isArrival
        ? 'base.flightSearch.terminalArriveWarningMessage'
        : 'base.flightSearch.terminalDepartWarningMessage',
    ),
    buttons: ['OK'],
  });

  await alert.present();
};

export const displayBookingFlightTimeAlert = async (dateTime: string) => {
  const alert = await alertController.create({
    header: t('base.flightSearch.matchFlightTimeHeader'),
    message: t('base.flightSearch.matchFlightTimeMessage'),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
      },
      {
        text: t('base.ok'),
        role: 'confirm',
        handler: () => {
          useStore().activeBooking.setValues = [
            {
              dataName: 'requiredDateTime',
              value: dateTime,
            },
            {
              dataName: 'asap',
              value: false,
            },
          ];
        },
      },
    ],
  });

  await alert.present();
};

export const displayMasqueradeFavBookingAlert = async (
  contact: Contact,
  continueFunction: () => Promise<void>,
) => {
  const alert = await alertController.create({
    header: t('base.areYouSure'),
    message: t('base.bookAsWarning', {
      contactName: contact.contactName,
    }),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
      },
      {
        text: t('base.ok'),
        role: 'confirm',
        handler: async () => {
          await useStore().setContact(StoreContactType.JOB, contact);
          await continueFunction();
        },
      },
    ],
  });

  await alert.present();
};

export const displayMasqueradeActionBookingAlert = async (contact: Contact) => {
  const store = useStore();
  const alert = await alertController.create({
    header: t('base.areYouSure'),
    message: t('base.bookAsWarning', {
      contactName: contact.contactName,
    }),
    buttons: [
      {
        text: t('base.cancel'),
        role: 'cancel',
        handler: async () => {
          await useStore().setContact(
            StoreContactType.JOB,
            integraUserToContact(store.integraProfile.user),
          );
          await router.replace({ name: 'home' });
        },
      },
      {
        text: t('base.ok'),
        role: 'confirm',
      },
    ],
  });

  await alert.present();
};

export const displayNoAddJobsAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.addJobsMessage', { companyTelephoneNumber }),
  );
};

export const displayNoEditJobsAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.editJobsMessage', { companyTelephoneNumber }),
  );
};

export const displayNoCancelJobsAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.cancelJobsMessage', { companyTelephoneNumber }),
  );
};

export const displayNoAddCardsAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.addCardsMessage', { companyTelephoneNumber }),
  );
};

export const displayNoEditContactsAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.editContactsMessage', { companyTelephoneNumber }),
  );
};

export const displayNoAddAddressesAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.addAddressesMessage', { companyTelephoneNumber }),
  );
};

export const displayNoEditAddressesAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.editAddressesMessage', { companyTelephoneNumber }),
  );
};

export const displayNoDeleteAddressesAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.editAddressesMessage', { companyTelephoneNumber }),
  );
};

export const displayNoCanRequestAccessAlert = async () => {
  await okAlertWithMessage(
    t('base.webAccess.canRequestMessage', { companyTelephoneNumber }),
  );
};

const setStateAndNavigate = (
  values: BookingFormState,
  goodsType: GoodsType,
  route: 'signin' | 'signup',
) => {
  const store = useStore();
  store.setQuickQuoteState(values, goodsType);
  router.replace({ name: route });
  store.activeBooking.bookingType = undefined;
};

export const displaySignInOrSignUpAlert = async (
  values: BookingFormState,
  goodsType: GoodsType,
) => {
  const alert = await alertController.create({
    header: `${t('auth.signIn')} / ${t('auth.signUp')}`,
    message: t('form.quickQuoteBookingMessage', { app: capConfig.appName }),
    buttons: [
      {
        text: t('auth.signIn'),
        role: 'confirm',
        handler: () => {
          setStateAndNavigate(values, goodsType, 'signin');
        },
      },
      {
        text: t('auth.signUp'),
        role: 'confirm',
        handler: () => {
          setStateAndNavigate(values, goodsType, 'signup');
        },
      },
    ],
  });

  await alert.present();
};

export const displayMFASelectionAlert =
  async (): Promise<MFAMethods | null> => {
    const alert = await alertController.create({
      header: t('auth.mfa.mfaSelectionHeader'),
      message: t('auth.mfa.mfaSelectionMessage'),
      buttons: [
        {
          text: t('auth.mfa.SMS'),
          role: MFAMethods.SMS,
        },
        {
          text: t('auth.mfa.TOTP'),
          role: MFAMethods.TOTP,
        },
        {
          text: t('base.cancel'),
          role: 'destructive',
          cssClass: 'alert-destructive',
        },
      ],
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    if (role === MFAMethods.SMS) return MFAMethods.SMS;
    if (role === MFAMethods.TOTP) return MFAMethods.TOTP;

    return null;
  };
